import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ReceiptDialog, { ReceiptRef } from '../../components/BridgeClub/ReceiptDialog';
import BridgeClubLayout from '../../components/BridgeClub/BridgeClubLayout';
import { bridgeClubAPI } from '../../services/bridgeClubAPI';
import './BridgeClubPurchases.css';

interface PurchaseItem {
  name: string;
  product_name?: string;  // Legacy field
  is_red_bridge: boolean;
  quantity: number;
  price: number;
  total_price?: number;  // Legacy field
}

interface Purchase {
  id: string;
  source: string;
  purchase_date: string;
  transaction_timestamp?: string | null;
  journal_number?: string | null;
  included_free_bag?: boolean;
  store?: { name: string; city: string };
  partner_store?: string;
  submission_type: string;
  verification_status: string;
  red_bridge_bags: number;
  loyalty_credited: number;
  loyalty_credit_applied?: boolean;
  total: number;
  items: PurchaseItem[];
  // What to open for the full receipt (Brad, 2026-09-23).
  detail?: ReceiptRef;
}

const BridgeClubPurchases: React.FC = () => {
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openReceipt, setOpenReceipt] = useState<ReceiptRef | null>(null);

  useEffect(() => {
    loadPurchases();
  }, []);

  const loadPurchases = async () => {
    try {
      const data = await bridgeClubAPI.getPurchases();
      setPurchases(data);
    } catch (err) {
      console.error('Failed to load purchases:', err);
      setError('Failed to load purchases');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <BridgeClubLayout>
        <div className="bridge-club-loading">
          <div className="bridge-club-spinner"></div>
          <p>Loading purchases...</p>
        </div>
      </BridgeClubLayout>
    );
  }

  return (
    <BridgeClubLayout>
      <div className="purchases-page">
        <div className="page-header">
          <h1>Purchase History</h1>
          <Link to="/bridge-club/upload" className="upload-btn">
            + Add Receipt
          </Link>
        </div>

        {error && <div className="error-message">{error}</div>}

        {purchases.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">🧾</span>
            <h3>No purchases yet</h3>
            <p>Scan a receipt to log your first purchase and start earning rewards!</p>
            <Link to="/bridge-club/upload" className="btn-primary">Scan Receipt</Link>
          </div>
        ) : (
          <div className="purchases-list">
            {purchases.map((purchase) => (
              <div
                key={purchase.id}
                className="purchase-card"
                role={purchase.detail ? 'button' : undefined}
                tabIndex={purchase.detail ? 0 : undefined}
                onClick={() => purchase.detail && setOpenReceipt(purchase.detail)}
                onKeyDown={(e) => { if (purchase.detail && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setOpenReceipt(purchase.detail); } }}
                style={purchase.detail ? { cursor: 'pointer' } : undefined}
              >
                <div className="purchase-header">
                  <div className="purchase-date">{formatDate(purchase.purchase_date)}</div>
                  <div className="purchase-total">${Number(purchase.total || 0).toFixed(2)}</div>
                </div>

                {purchase.store && (
                  <div className="purchase-store">
                    📍 {purchase.store.name}{purchase.store.city ? `, ${purchase.store.city}` : ''}
                  </div>
                )}

                {(purchase.transaction_timestamp || purchase.journal_number) && (
                  <div className="purchase-txn-meta" style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
                    {purchase.transaction_timestamp && (
                      <span>{formatDate(purchase.transaction_timestamp)} at {formatTime(purchase.transaction_timestamp)}</span>
                    )}
                    {purchase.journal_number && (
                      <span>{purchase.transaction_timestamp ? ' • ' : ''}Receipt #{purchase.journal_number}</span>
                    )}
                  </div>
                )}

                <div className="purchase-items">
                  {purchase.items.slice(0, 3).map((item, idx) => (
                    <div key={idx} className="purchase-item">
                      <span className={`item-name ${item.is_red_bridge ? 'red-bridge' : ''}`}>
                        {item.name || item.product_name}
                        {item.is_red_bridge && ' ✓'}
                      </span>
                      <span className="item-qty">{item.quantity}x</span>
                    </div>
                  ))}
                  {purchase.items.length > 3 && (
                    <div className="more-items">
                      +{purchase.items.length - 3} more items
                    </div>
                  )}
                </div>

                {/* Show if free bag was redeemed */}
                {purchase.loyalty_credit_applied && (
                  <div className="purchase-loyalty redeemed">
                    <span className="loyalty-icon">🎁</span>
                    <span>FREE BAG redeemed</span>
                  </div>
                )}

                {/* Show stamps earned (bags that count toward next free bag) */}
                {(purchase.red_bridge_bags > 0 || purchase.loyalty_credited > 0) && !purchase.loyalty_credit_applied && (
                  <div className="purchase-loyalty">
                    <span className="loyalty-icon">🎫</span>
                    <span>
                      {(purchase.loyalty_credited || purchase.red_bridge_bags) === 1
                        ? 'Stamp earned'
                        : `${purchase.loyalty_credited || purchase.red_bridge_bags} stamps earned`}
                    </span>
                  </div>
                )}

                <div className="purchase-meta">
                  <span className={`verification-badge ${purchase.verification_status}`}>
                    {purchase.verification_status === 'auto_verified' && '✓ Verified'}
                    {purchase.verification_status === 'pending' && '⏳ Pending'}
                    {purchase.verification_status === 'verified' && '✓ Verified'}
                    {purchase.verification_status === 'rejected' && '✗ Rejected'}
                  </span>
                  {purchase.source === 'partner_api' ? (
                    <span className="submission-type partner-api">
                      🔗 Via {purchase.partner_store || purchase.store?.name || 'Partner'}
                    </span>
                  ) : purchase.source === 'pos' ? (
                    <span className="submission-type">🏪 Red Bridge Farm store</span>
                  ) : purchase.source === 'delivery' ? (
                    <span className="submission-type">🚚 Home delivery</span>
                  ) : (
                    <span className="submission-type">
                      {purchase.submission_type === 'self_upload' ? '📱 Self-upload' : '🏪 In-store'}
                    </span>
                  )}
                </div>
                {purchase.detail && (
                  <div style={{ marginTop: 8, color: '#B91C1C', fontWeight: 600, fontSize: 14 }}>See the full receipt →</div>
                )}
              </div>
            ))}
          </div>
        )}
        {openReceipt && <ReceiptDialog receipt={openReceipt} onClose={() => setOpenReceipt(null)} />}
      </div>
    </BridgeClubLayout>
  );
};

export default BridgeClubPurchases;
